import { ContractTypes } from '@draftkings/dk-data-layer';
import {
    IDataIndex,
    Localization,
    SelectionHandlerIDs,
    MarketBuilderConfig,
    MarketWithSelections,
    marketCellsBuilder
} from '@draftkings/component-builder';
import { OddsStyles } from '@draftkings/event-page-widget-contracts';
import { parseScoreboards } from '@draftkings/scoreboards';
import { getDefaultValue } from '@draftkings/sportsbook-common-utils';
import { ParsedEvent } from '../../../contracts/parsers/IParser';
import { compareByCategoryThenSubcategory, EVENT_PAGE } from '../../../helpers';
import {
    FeatureFlags,
    Logos,
    LogosMap,
    ProductConfig,
    LocalizationConfig
} from '@draftkings/sportsbook-common-contracts';
import { marketBoardDict } from '../../../helpers/dict';

export type ParseEventArgs = {
    event: ContractTypes.SportEvent;
    marketsWithSelectionsBySubId: Record<string, MarketWithSelections[]>;
    isTeamSwap: boolean;
    categoriesSortOrderMap: Map<string, number>;
    localization: Localization;
    localizationConfig: LocalizationConfig;
    loadingSelectionIndex: IDataIndex<string, boolean>;
    selectedSelectionIndex: IDataIndex<string, boolean>;
    oddsStyle: OddsStyles;
    onSelectionClick: (selectionIds: SelectionHandlerIDs) => void;
    logosMap: LogosMap;
    leagueName: string;
    staticS3Host: string;
    collapsedSubcategoryIds: Set<string>;
    logError: MarketBuilderConfig['logError'];
    featureFlags: FeatureFlags;
    productConfig: ProductConfig;
    logos: Logos;
};

export const parseEvent = ({
    event,
    marketsWithSelectionsBySubId,
    isTeamSwap,
    localization,
    localizationConfig,
    loadingSelectionIndex,
    selectedSelectionIndex,
    oddsStyle,
    onSelectionClick,
    logosMap,
    leagueName,
    staticS3Host,
    collapsedSubcategoryIds,
    logError,
    categoriesSortOrderMap,
    featureFlags,
    productConfig,
    logos
}: ParseEventArgs): ParsedEvent => {
    return {
        ...event,
        leagueName,
        scoreboardData: parseScoreboards({
            event,
            logosMap,
            staticS3Host,
            isTeamSwap,
            isSwooshTeamLogosEnabled: featureFlags['RLS-OSB-WEB-IsSwooshTeamLogosEnabled']['EventPage'],
            swooshBaseUrl: productConfig.swooshBaseUrl
        }),
        subcategories: getDefaultValue(
            event.subcategories?.slice().map((subcategory) => {
                return {
                    ...subcategory,
                    subcategorySortingKeys: {
                        categorySortOrder: getDefaultValue(
                            categoriesSortOrderMap.get(subcategory.categoryId),
                            Number.MAX_SAFE_INTEGER
                        ),
                        subcategorySortOrder: subcategory.sortOrder
                    },
                    isExpanded: !collapsedSubcategoryIds.has(subcategory.id),
                    marketsCells: marketCellsBuilder({
                        eventId: event.id,
                        eventStatus: event.status,
                        markets: marketsWithSelectionsBySubId[subcategory.id] || [],
                        onSelectionClick,
                        categoryId: subcategory.categoryId,
                        subCategoryId: subcategory.id,
                        componentId: subcategory.componentId,
                        localization,
                        localizationConfig,
                        loadingSelectionIndex,
                        selectedSelectionIndex,
                        oddsStyle,
                        isTeamSwap,
                        participants: getDefaultValue(event.participants?.slice(), []),
                        logError,
                        featureFlags,
                        productConfig,
                        consumerPageName: EVENT_PAGE,
                        logos,
                        staticS3Host,
                        dict: marketBoardDict
                    }),
                    showSgpBadge:
                        !!subcategory.tags?.includes('SGP') && featureFlags['RLS-OSB-WEB-SGPTagsOnMarketLevelEnabled']
                };
            }),
            []
        )
            .sort(compareByCategoryThenSubcategory)
            .filter((m) => m.marketsCells.length > 0)
    };
};
